import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaMicrophone, FaMicrophoneSlash,
  FaVideo, FaVideoSlash,
  FaPhoneSlash, FaVolumeUp,
} from 'react-icons/fa';
import { getAvatarUrl } from '../../utils/avatar';

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const ActiveCallScreen = ({
  callInfo, localStream, remoteStream,
  isMuted, isVideoOff, callDuration,
  onToggleMute, onToggleVideo, onEndCall,
}) => {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const isVideo = callInfo?.callType === 'video';

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-gray-950 flex flex-col"
      data-testid="active-call-screen"
    >
      {/* Video call layout */}
      {isVideo ? (
        <div className="flex-1 relative bg-black">
          {/* Remote video (full screen) */}
          {remoteStream ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
              data-testid="remote-video"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <img
                  src={getAvatarUrl(callInfo?.otherUser?.avatar_url, callInfo?.otherUser?.full_name)}
                  alt=""
                  className="w-32 h-32 rounded-full mx-auto mb-4 border-4 border-gray-700"
                />
                <p className="text-gray-400 text-sm">Connecting video...</p>
              </div>
            </div>
          )}

          {/* Local video (picture-in-picture) */}
          {localStream && !isVideoOff && (
            <div className="absolute top-4 right-4 w-32 h-44 rounded-2xl overflow-hidden border-2 border-gray-700 shadow-2xl" data-testid="local-video-pip">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover mirror"
                style={{ transform: 'scaleX(-1)' }}
              />
            </div>
          )}

          {/* Call info overlay */}
          <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-md rounded-2xl px-4 py-2">
            <p className="text-white font-medium text-sm">{callInfo?.otherUser?.full_name}</p>
            <p className="text-green-400 text-xs font-mono" data-testid="call-duration">{formatDuration(callDuration)}</p>
          </div>
        </div>
      ) : (
        /* Voice call layout */
        <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-b from-gray-900 to-gray-950">
          <div className="relative mb-6">
            <div className="absolute inset-[-4px] rounded-full bg-green-500/20 animate-pulse" />
            <img
              src={getAvatarUrl(callInfo?.otherUser?.avatar_url, callInfo?.otherUser?.full_name)}
              alt={callInfo?.otherUser?.full_name}
              className="relative w-32 h-32 rounded-full object-cover border-4 border-green-500/30"
              data-testid="voice-call-avatar"
            />
          </div>
          <h2 className="text-white text-2xl font-bold mb-1">{callInfo?.otherUser?.full_name}</h2>
          <p className="text-gray-400 text-sm mb-2">@{callInfo?.otherUser?.username}</p>
          <p className="text-green-400 text-lg font-mono" data-testid="call-duration">{formatDuration(callDuration)}</p>

          {/* Audio visualizer dots */}
          <div className="flex gap-1 mt-6">
            {[...Array(5)].map((_, i) => (
              <div
                key={`viz-${i}`}
                className="w-1.5 bg-green-400/60 rounded-full animate-pulse"
                style={{
                  height: `${12 + Math.random() * 20}px`,
                  animationDelay: `${i * 0.15}s`,
                  animationDuration: '0.8s',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Controls bar */}
      <div className="bg-gray-900/90 backdrop-blur-lg border-t border-gray-800 px-6 py-6 safe-bottom">
        <div className="flex items-center justify-center gap-6">
          {/* Mute button */}
          <button
            onClick={onToggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
              isMuted ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
            data-testid="toggle-mute-btn"
          >
            {isMuted ? <FaMicrophoneSlash size={20} /> : <FaMicrophone size={20} />}
          </button>

          {/* Video toggle (only for video calls) */}
          {isVideo && (
            <button
              onClick={onToggleVideo}
              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                isVideoOff ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
              data-testid="toggle-video-btn"
            >
              {isVideoOff ? <FaVideoSlash size={20} /> : <FaVideo size={20} />}
            </button>
          )}

          {/* Speaker (decorative for now) */}
          <button
            className="w-14 h-14 rounded-full bg-gray-800 text-white flex items-center justify-center hover:bg-gray-700 transition-all"
            data-testid="speaker-btn"
          >
            <FaVolumeUp size={20} />
          </button>

          {/* End call */}
          <button
            onClick={onEndCall}
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all hover:scale-110 shadow-lg shadow-red-500/30"
            data-testid="end-call-btn"
          >
            <FaPhoneSlash className="text-white text-xl" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ActiveCallScreen;
